import { fetchWrapperPortal } from './fetch';
import { setGlobalUser, resetGlobalUser } from 'stores';
import { clearAuthStorage } from './sessionStorage';
import type { User, SignupInputs, UpdateUserPayload, UserStats, PasswordChangePayload } from '../../types';

interface UserResponse {
  success: boolean;
  user: User;
}

export const signup = async (user: SignupInputs): Promise<User> =>
  await fetchWrapperPortal<User>({
    url: `users/`,
    method: `POST`,
    body: JSON.stringify(user),
  });

export const createOther = async (user: Partial<User>): Promise<UserResponse> => {
  return await fetchWrapperPortal<UserResponse>({
    url: `users/new`,
    method: `POST`,
    addBearer: true,
    body: JSON.stringify(user),
  });
};

export const addToMyOrg = async (user: Partial<User>): Promise<UserResponse> => {
  return await fetchWrapperPortal<UserResponse>({
    url: `users/myorg/`,
    method: `POST`,
    addBearer: true,
    body: JSON.stringify(user),
  });
};

export const update = async (userId: string, payload: UpdateUserPayload): Promise<User | null> => {
  let res = await fetchWrapperPortal<UserResponse>({
    url: `users/` + userId,
    method: 'PUT',
    addBearer: true,
    body: JSON.stringify(payload),
  });
  if (res.success) {
    persistUser(res.user);
    return res.user;
  } else {
    return null;
  }
};

export const updateOther = async (userId: string, payload: UpdateUserPayload): Promise<User | UserResponse> => {
  let res = await fetchWrapperPortal<UserResponse>({
    url: `users/` + userId,
    method: 'PUT',
    addBearer: true,
    body: JSON.stringify(payload),
  });
  if (res.success) {
    return res.user;
  } else {
    return res;
  }
};

export const del = async (userId: string): Promise<User | null> => {
  let res = await fetchWrapperPortal<UserResponse>({
    url: `users/` + userId,
    method: 'DELETE',
    addBearer: true,
  });
  if (res.success) {
    return res.user;
  } else {
    return null;
  }
};

export const resendInvite = async (userId: string): Promise<boolean> => {
  let res = await fetchWrapperPortal<UserResponse>({
    url: `users/` + userId + `/resend-invite`,
    method: 'POST',
    addBearer: true,
  });
  if (res.success) {
    return true;
  } else {
    return false;
  }
};

export const uploadImage = async (file: File, type: string): Promise<string> => {
  const result = await fetchWrapperPortal<{ put_url: string; get_url: string }>({
    url: `users/image_upload`,
    method: 'POST',
    addBearer: true,
    body: JSON.stringify({
      content_type: file.type,
      image_type: type,
    }),
  });

  return await fetch(result.put_url, {
    method: 'PUT',
    body: file,
  }).then(e => {
    if (e.ok) {
      return result.get_url;
    }
    throw e;
  });
};

export const getStats = async (id: string, start?: string, end?: string): Promise<UserStats> =>
  await fetchWrapperPortal<UserStats>({
    url: `users/${id}/stats/${start ? `?start=${start}` : ''}${end ? `&end=${end}` : ''}`,
    method: 'GET',
    addBearer: true,
  });

export const refreshUser = async (): Promise<User | null> => {
  try {
    const user = await fetchWrapperPortal<User>({
      url: `users/me`,
      method: `GET`,
      addBearer: true,
    });
    if (user && user._id) {
      persistUser(user);
      return user;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export function persistUser(user: User | null): void {
  if (user) {
    localStorage.setItem('user', JSON.stringify(user));
    setGlobalUser(user);
  } else {
    resetGlobalUser();
    clearAuthStorage();
  }
}

export const getBanners = async (userId: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `users/${userId}/banners/`,
    method: `GET`,
    addBearer: true,
  });

export const changePassword = async (payload: PasswordChangePayload): Promise<any> =>
  await fetchWrapperPortal({
    url: 'users/me/password/',
    method: 'PUT',
    addBearer: true,
    body: JSON.stringify(payload),
  });

export const makeMod = async (broadcaster: string, moderator: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `users/${broadcaster}/moderators/${moderator}`,
    method: 'PUT',
    addBearer: true,
    body: JSON.stringify({ broadcaster, moderator }),
  });

export const removeMod = async (broadcaster: string, moderator: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `users/${broadcaster}/moderators/${moderator}`,
    method: 'DELETE',
    addBearer: true,
    body: JSON.stringify({}),
  });

export const sendResetEmail = async (email: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `auth/password/reset/`,
    method: 'POST',
    body: JSON.stringify({ email: email }),
  });

export const setNewPassword = async (token: string, password: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `auth/password/reset/${token}`,
    method: 'PUT',
    body: JSON.stringify({ password: password }),
  });

export const getAll = async (): Promise<User[]> =>
  await fetchWrapperPortal<User[]>({
    url: `users/`,
    method: `GET`,
    addBearer: true,
  });

export const getAllFromMyOrg = async (): Promise<User[]> =>
  await fetchWrapperPortal<User[]>({
    url: `users/myorg/`,
    method: `GET`,
    addBearer: true,
  });

export const getBlockedUsers = async (): Promise<User[]> =>
  await fetchWrapperPortal<User[]>({
    url: `users/blocked/`,
    method: `GET`,
    addBearer: true,
  });

export const setBlockedStatus = async (userId: string, blockFlag: boolean, streamId: string): Promise<any> => {
  return await fetchWrapperPortal({
    url: `users/setBlockedStatus/`,
    method: `POST`,
    addBearer: true,
    body: JSON.stringify({ userId, blockFlag, streamId }),
  });
};

export const checkCreator = async (id: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `users/${id}/link-check`,
    method: 'GET',
    addBearer: true,
  });

export const getUserActiveAdvancedStreams = async (userId: string): Promise<any> =>
  await fetchWrapperPortal({
    url: `users/${userId}/active-advanced-streams`,
    method: 'GET',
    addBearer: true,
  });
